import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Coins, User, LogOut, LayoutDashboard, History, Trophy, Crown, Gift, Users, TrendingUp, Ticket, Sparkles, ChevronDown, Spade, Flame, Type, Send, LifeBuoy, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useBigText } from '../hooks/useBigText';
import { useAuth } from '../contexts/AuthContext';
import { useFeatures } from '../hooks/useFeatures';
import { Button } from '../components/ui/button';
import NotificationBell from './NotificationBell';
import CashOutDialog from './CashOutDialog';
import RebateCountdownChip from './RebateCountdownChip';
import PayoutHoursChip from './PayoutHoursChip';
import JackpotTicker from './JackpotTicker';  // eslint-disable-line no-unused-vars -- jackpots removed from UI (Jun 2026)
import LanguageToggle from './LanguageToggle';
import { onPotContribution } from '../utils/potContribution';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const Header = () => {
    const { user, logout } = useAuth();
    const features = useFeatures();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const { enabled: bigText, toggle: toggleBigText } = useBigText();
    const { t } = useTranslation();

    const lowBalance = user && user.role !== 'admin' && (Number(user.credits ?? 0) + Number(user.bonus_credits ?? 0)) < 10;

    const navLinks = [
        { name: t('nav.sportsbook'), href: '/sportsbook', icon: TrendingUp, roles: ['user', 'operator', 'admin', 'ambassador'], feature: 'sportsbook' },
        { name: t('nav.casino'),     href: '/casino/slots',     icon: Spade,      roles: ['user', 'operator', 'admin', 'ambassador'], feature: 'casino' },
        { name: t('nav.lottery'),    href: '/lottery',    icon: Ticket,     roles: ['user', 'operator', 'admin', 'ambassador'], feature: 'lottery' },
        {
            name: t('nav.perks'),
            icon: Sparkles,
            roles: ['user', 'operator', 'admin', 'ambassador'],
            children: [
                { name: t('nav.rewards'),     href: '/rewards',     icon: Gift,   description: 'Points & tier perks' },
                { name: 'Tournaments', href: '/tournaments', icon: Trophy, description: 'Compete for prizes', feature: 'tournaments' },
                { name: t('nav.referrals'),   href: '/referrals',   icon: Users,  description: 'Invite friends, earn credits' },
            ],
        },
        { name: t('nav.transactions'), href: '/transactions', icon: History, roles: ['user', 'operator', 'admin', 'distributor', 'ambassador'] },
    ];

    // Apply feature gating: drop top-level nav items disabled for this user's operator,
    // and prune children groups whose individual items are gated off.
    const filteredNavLinks = navLinks
        .filter(link => !link.roles || link.roles.includes(user?.role))
        .filter(link => !link.feature || features[link.feature] !== false)
        .map(link => link.children
            ? { ...link, children: link.children.filter(c => !c.feature || features[c.feature] !== false) }
            : link)
        .filter(link => !link.children || link.children.length > 0);

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    return (
        <header className="sticky top-0 z-50 glass border-b border-white/10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo — players go to /lobby, admins to /admin, guests to / */}
                    <Link
                        to={user ? (user.role === 'admin' ? '/admin' : '/lobby') : '/'}
                        className="flex items-center gap-2"
                        data-testid="header-logo"
                    >
                        <img
                            src="/betohalics-logo.png"
                            alt="Betohalics — Real Games. Real People. Real Wins."
                            className="h-10 sm:h-12 lg:h-14 w-auto object-contain"
                            style={{ filter: 'drop-shadow(0 0 12px rgba(255,215,0,0.28))' }}
                            data-testid="header-logo-img"
                        />
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden md:flex items-center gap-3 lg:gap-5 overflow-x-auto max-w-[55vw] lg:max-w-none scrollbar-hide">
                        {filteredNavLinks.map((link) => {
                            // Group with dropdown
                            if (link.children) {
                                const isActive = link.children.some(c => location.pathname === c.href);
                                return (
                                    <DropdownMenu key={link.name}>
                                        <DropdownMenuTrigger
                                            className={`flex items-center gap-1.5 text-xs lg:text-sm font-medium transition-colors whitespace-nowrap outline-none ${
                                                isActive ? 'text-[#FFD700]' : 'text-[#A0AAB2] hover:text-white'
                                            }`}
                                            data-testid={`nav-${link.name.toLowerCase()}`}
                                        >
                                            <link.icon className="w-4 h-4" />
                                            {link.name}
                                            <ChevronDown className="w-3 h-3 -ml-0.5 opacity-70" />
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent
                                            align="end"
                                            className="bg-[#0a0a0a] border-white/10 min-w-[220px]"
                                        >
                                            {link.children.map((child) => {
                                                const childActive = location.pathname === child.href;
                                                return (
                                                    <DropdownMenuItem
                                                        key={child.href}
                                                        asChild
                                                        className={`cursor-pointer focus:bg-[#FFD700]/10 ${
                                                            childActive ? 'bg-[#FFD700]/10 text-[#FFD700]' : 'text-white'
                                                        }`}
                                                    >
                                                        <Link
                                                            to={child.href}
                                                            className="flex items-start gap-3 px-3 py-2.5"
                                                            data-testid={`nav-${child.name.toLowerCase()}`}
                                                        >
                                                            <child.icon className={`w-4 h-4 mt-0.5 shrink-0 ${childActive ? 'text-[#FFD700]' : 'text-[#A0AAB2]'}`} />
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-sm font-bold leading-tight">{child.name}</p>
                                                                {child.description && (
                                                                    <p className="text-[10px] text-[#A0AAB2] leading-tight">{child.description}</p>
                                                                )}
                                                            </div>
                                                        </Link>
                                                    </DropdownMenuItem>
                                                );
                                            })}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                );
                            }
                            // Flat link
                            return (
                                <Link
                                    key={link.href}
                                    to={link.href}
                                    className={`flex items-center gap-1.5 text-xs lg:text-sm font-medium transition-colors whitespace-nowrap ${
                                        location.pathname === link.href
                                            ? 'text-[#FFD700]'
                                            : 'text-[#A0AAB2] hover:text-white'
                                    }`}
                                    data-testid={`nav-${link.name.toLowerCase()}`}
                                >
                                    <link.icon className="w-4 h-4" />
                                    {link.name}
                                </Link>
                            );
                        })}
                        {user?.role === 'admin' && (
                            <Link
                                to="/admin"
                                className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                                    location.pathname.startsWith('/admin')
                                        ? 'text-[#FFD700]'
                                        : 'text-[#A0AAB2] hover:text-white'
                                }`}
                                data-testid="nav-admin"
                            >
                                <LayoutDashboard className="w-4 h-4" />
                                Admin
                            </Link>
                        )}
                        {(user?.role === 'ambassador' || user?.is_ambassador) && (
                            <Link
                                to="/affiliate"
                                className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                                    location.pathname.startsWith('/affiliate')
                                        ? 'text-[#FFD700]'
                                        : 'text-[#A0AAB2] hover:text-white'
                                }`}
                                data-testid="nav-ambassador-portal"
                            >
                                <Crown className="w-4 h-4" />
                                Ambassador Portal
                            </Link>
                        )}
                    </nav>

                    {/* Right side */}
                    <div className="flex items-center gap-4">
                        {/* Credits Display with VIP Badge — players only (admins don't have a wallet) */}
                        {user && user.role !== 'admin' && (
                            <div className="hidden sm:flex items-center gap-3">
                                {/* VIP Tier Badge (display-only — VIP page hidden) */}
                                {user.vip_name && (
                                    <div
                                        className="px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1"
                                        style={{ 
                                            backgroundColor: user.vip_color + '20',
                                            color: user.vip_color,
                                            border: `1px solid ${user.vip_color}40`
                                        }}
                                        data-testid="vip-badge"
                                    >
                                        <Crown className="w-3 h-3" />
                                        {user.vip_name}
                                    </div>
                                )}
                                {/* Shared slots jackpot — visible on every screen */}
                                <HeaderJackpotChip variant="desktop" />
                                {/* Credits */}
                                <CreditsChip credits={user.credits} bonusCredits={user.bonus_credits} variant="desktop" />
                                {/* Buy Credits — PayPal checkout. Jumps out when balance is low. */}
                                <motion.div className="relative"
                                    animate={lowBalance ? { scale: [1, 1.06, 1] } : { scale: 1 }}
                                    transition={lowBalance ? { duration: 1.2, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.2 }}>
                                    {lowBalance && (
                                        <span className="absolute -top-2 -right-1 z-10 text-[9px] font-black bg-[#FF3B30] text-white px-1.5 py-0.5 rounded-full shadow-lg animate-pulse" data-testid="low-balance-badge">LOW</span>
                                    )}
                                    <Button size="sm" onClick={() => window.location.assign('/buy-credits')} data-testid="buy-credits-btn-desktop"
                                        className={`text-black font-bold hover:opacity-90 rounded-full px-4 ${lowBalance ? 'bg-gradient-to-r from-[#FF8C00] to-[#FF3B30] ring-2 ring-[#FFD700] shadow-[0_0_18px_rgba(255,140,0,0.75)]' : 'bg-gradient-to-r from-[#FFD700] to-[#FF8C00]'}`}>
                                        <Coins className="w-4 h-4 mr-1" /> Buy Credits
                                    </Button>
                                </motion.div>
                                {/* Weekly cashout-fee rebate countdown ("🎁 $X coming Mon") */}
                                <RebateCountdownChip variant="desktop" />
                                {/* After-hours payout desk countdown — only visible outside 8AM–10PM CT */}
                                <PayoutHoursChip variant="desktop" />
                                {/* Pot contribution flash */}
                                <PotContributionChip />
                                {/* Cash Out quick-access */}
                                <CashOutDialog compact />
                            </div>
                        )}

                        {/* Notification Bell */}
                        {user && <NotificationBell />}

                        {/* Language toggle — EN ↔ ES */}
                        <LanguageToggle compact />

                        {/* User Menu */}
                        {user ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button 
                                        variant="ghost" 
                                        className="flex items-center gap-2 text-white hover:text-[#FFD700] hover:bg-white/5"
                                        data-testid="user-menu-trigger"
                                    >
                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FF007F] flex items-center justify-center">
                                            <User className="w-4 h-4 text-black" />
                                        </div>
                                        <span className="hidden sm:inline font-medium">{user.name}</span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56 bg-[#121212] border-white/10">
                                    <DropdownMenuLabel className="text-[#A0AAB2]">
                                        <div className="flex flex-col">
                                            <span className="text-white font-medium">{user.name}</span>
                                            <span className="text-xs">{user.email}</span>
                                        </div>
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator className="bg-white/10" />
                                    <DropdownMenuItem 
                                        onClick={() => navigate('/profile')}
                                        className="text-white hover:text-[#FFD700] hover:bg-white/5 cursor-pointer"
                                        data-testid="menu-profile"
                                    >
                                        <User className="mr-2 h-4 w-4" />
                                        Profile
                                    </DropdownMenuItem>
                                    <DropdownMenuItem 
                                        onClick={() => navigate('/transactions')}
                                        className="text-white hover:text-[#FFD700] hover:bg-white/5 cursor-pointer"
                                        data-testid="menu-transactions"
                                    >
                                        <History className="mr-2 h-4 w-4" />
                                        Transactions
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onClick={() => navigate('/send')}
                                        className="text-white hover:text-[#FFD700] hover:bg-white/5 cursor-pointer"
                                        data-testid="menu-send-credits"
                                    >
                                        <Send className="mr-2 h-4 w-4" />
                                        Send Credits
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onClick={() => navigate('/help')}
                                        className="text-white hover:text-[#FFD700] hover:bg-white/5 cursor-pointer"
                                        data-testid="menu-help"
                                    >
                                        <LifeBuoy className="mr-2 h-4 w-4" />
                                        Help Center
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onClick={() => { window.dispatchEvent(new Event('open-support-chat')); }}
                                        className="text-white hover:text-[#FFD700] hover:bg-white/5 cursor-pointer"
                                        data-testid="menu-messages"
                                    >
                                        <MessageCircle className="mr-2 h-4 w-4" />
                                        Messages
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onSelect={(e) => { e.preventDefault(); toggleBigText(); }}
                                        className="text-white hover:text-[#FFD700] hover:bg-white/5 cursor-pointer flex items-center justify-between"
                                        data-testid="menu-bigger-text"
                                    >
                                        <span className="flex items-center">
                                            <Type className="mr-2 h-4 w-4" />
                                            Bigger Text
                                        </span>
                                        <span
                                            className={`text-[10px] font-black px-2 py-0.5 rounded-full ${bigText ? 'bg-[#39FF14] text-black' : 'bg-white/10 text-[#A0AAB2]'}`}
                                            data-testid="menu-bigger-text-state"
                                        >
                                            {bigText ? 'ON' : 'OFF'}
                                        </span>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator className="bg-white/10" />
                                    <DropdownMenuItem 
                                        onClick={handleLogout}
                                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer"
                                        data-testid="menu-logout"
                                    >
                                        <LogOut className="mr-2 h-4 w-4" />
                                        {t('common.logout')}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="ghost"
                                    onClick={() => navigate('/login')}
                                    className="text-white hover:text-[#FFD700]"
                                    data-testid="header-login"
                                >
                                    Login
                                </Button>
                                <Button
                                    onClick={() => navigate('/register')}
                                    className="btn-primary px-4 py-2 rounded-full"
                                    data-testid="header-register"
                                >
                                    Sign Up
                                </Button>
                            </div>
                        )}

                        {/* Mobile menu button */}
                        <button
                            className="md:hidden p-2 text-white"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            data-testid="mobile-menu-btn"
                        >
                            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu */}
            <AnimatePresence>
                {mobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="md:hidden border-t border-white/10 bg-[#0a0a0a]"
                    >
                        <div className="px-4 py-4 space-y-2">
                            {user && user.role !== 'admin' && (
                                <>
                                <div className="flex items-center justify-between gap-2 mb-3">
                                    <HeaderJackpotChip variant="mobile" />
                                </div>
                                <div className="flex items-center justify-between gap-2 mb-4">
                                    <CreditsChip credits={user.credits} bonusCredits={user.bonus_credits} variant="mobile" />
                                    <motion.div className="relative"
                                        animate={lowBalance ? { scale: [1, 1.06, 1] } : { scale: 1 }}
                                        transition={lowBalance ? { duration: 1.2, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.2 }}>
                                        {lowBalance && (
                                            <span className="absolute -top-2 -right-1 z-10 text-[9px] font-black bg-[#FF3B30] text-white px-1.5 py-0.5 rounded-full shadow-lg animate-pulse" data-testid="low-balance-badge-mobile">LOW</span>
                                        )}
                                        <Button size="sm" onClick={() => window.location.assign('/buy-credits')} data-testid="buy-credits-btn-mobile"
                                            className={`text-black font-bold hover:opacity-90 rounded-full px-3 ${lowBalance ? 'bg-gradient-to-r from-[#FF8C00] to-[#FF3B30] ring-2 ring-[#FFD700] shadow-[0_0_16px_rgba(255,140,0,0.75)]' : 'bg-gradient-to-r from-[#FFD700] to-[#FF8C00]'}`}>
                                            <Coins className="w-4 h-4 mr-1" /> Buy Credits
                                        </Button>
                                    </motion.div>
                                    <CashOutDialog compact />
                                </div>
                                <div className="mb-4 -mt-2 flex justify-center gap-2 flex-wrap">
                                    <RebateCountdownChip variant="mobile" />
                                    <PayoutHoursChip variant="mobile" />
                                </div>
                                </>
                            )}
                            {filteredNavLinks.map((link) => {
                                if (link.children) {
                                    return (
                                        <div key={link.name} className="space-y-1">
                                            <div className="flex items-center gap-2 px-4 pt-2 pb-1 text-[10px] uppercase font-bold text-[#A0AAB2] tracking-wider">
                                                <link.icon className="w-3.5 h-3.5" />
                                                {link.name}
                                            </div>
                                            {link.children.map(child => (
                                                <Link
                                                    key={child.href}
                                                    to={child.href}
                                                    onClick={() => setMobileMenuOpen(false)}
                                                    className={`flex items-center gap-3 px-6 py-2.5 rounded-lg transition-colors ${
                                                        location.pathname === child.href
                                                            ? 'bg-[#FFD700]/10 text-[#FFD700]'
                                                            : 'text-white hover:bg-white/5'
                                                    }`}
                                                >
                                                    <child.icon className="w-4 h-4" />
                                                    <span className="text-sm">{child.name}</span>
                                                </Link>
                                            ))}
                                        </div>
                                    );
                                }
                                return (
                                    <Link
                                        key={link.href}
                                        to={link.href}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                                            location.pathname === link.href
                                                ? 'bg-[#FFD700]/10 text-[#FFD700]'
                                                : 'text-white hover:bg-white/5'
                                        }`}
                                    >
                                        <link.icon className="w-5 h-5" />
                                        {link.name}
                                    </Link>
                                );
                            })}
                            {user?.role === 'admin' && (
                                <Link
                                    to="/admin"
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                                        location.pathname.startsWith('/admin')
                                            ? 'bg-[#FFD700]/10 text-[#FFD700]'
                                            : 'text-white hover:bg-white/5'
                                    }`}
                                >
                                    <LayoutDashboard className="w-5 h-5" />
                                    Admin Dashboard
                                </Link>
                            )}
                            {(user?.role === 'ambassador' || user?.is_ambassador) && (
                                <Link
                                    to="/affiliate"
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                                        location.pathname.startsWith('/affiliate')
                                            ? 'bg-[#FFD700]/10 text-[#FFD700]'
                                            : 'text-white hover:bg-white/5'
                                    }`}
                                    data-testid="mobile-nav-ambassador-portal"
                                >
                                    <Crown className="w-5 h-5" />
                                    Ambassador Portal
                                </Link>
                            )}
                            {user && (
                                <button
                                    type="button"
                                    onClick={toggleBigText}
                                    className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg transition-colors text-white hover:bg-white/5 min-h-[48px]"
                                    data-testid="mobile-menu-bigger-text"
                                >
                                    <span className="flex items-center gap-3">
                                        <Type className="w-5 h-5" />
                                        Bigger Text
                                    </span>
                                    <span className={`text-xs font-black px-2.5 py-1 rounded-full ${bigText ? 'bg-[#39FF14] text-black' : 'bg-white/10 text-[#A0AAB2]'}`}>
                                        {bigText ? 'ON' : 'OFF'}
                                    </span>
                                </button>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Header;


const JP_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * HeaderJackpotChip — compact combined shared-slots jackpot shown in the top nav
 * so the live pot is visible on every screen. Tapping it opens the Slots lobby.
 */
const HeaderJackpotChip = ({ variant = 'desktop' }) => {
    const navigate = useNavigate();
    const [total, setTotal] = useState(null);
    useEffect(() => {
        let alive = true;
        const load = () => axios.get(`${JP_API}/games/balls-of-fire/jackpots`)
            .then(({ data }) => {
                if (!alive) return;
                const t = Object.values(data?.tiers || {}).reduce((s, x) => s + Number(x.current || 0), 0);
                setTotal(t);
            })
            .catch(() => {});
        load();
        const iv = setInterval(load, 15000);
        return () => { alive = false; clearInterval(iv); };
    }, []);

    if (!total) return null;
    const amount = `$${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    if (variant === 'mobile') {
        return (
            <button onClick={() => navigate('/casino/slots')} data-testid="header-jackpot-chip-mobile"
                className="flex-1 flex items-center justify-center gap-2 rounded-lg border border-[#FFD700]/40 bg-[#141006] px-3 py-2.5">
                <Crown className="h-4 w-4 text-[#FFD700]" />
                <span className="text-[9px] font-black uppercase tracking-wider text-[#FFD700]/70">Jackpot</span>
                <span className="font-mono text-sm font-black text-[#FFD700]">{amount}</span>
            </button>
        );
    }
    return (
        <button onClick={() => navigate('/casino/slots')} data-testid="header-jackpot-chip"
            title="Shared slots jackpot — tap to play"
            className="hidden md:flex items-center gap-1.5 rounded-full border border-[#FFD700]/40 bg-[#141006] px-3 py-1.5 transition-colors hover:border-[#FFD700]/80 hover:bg-[#1c1408]">
            <Crown className="h-4 w-4 text-[#FFD700]" />
            <span className="flex flex-col leading-none">
                <span className="text-[8px] font-black uppercase tracking-wider text-[#FFD700]/60">Jackpot</span>
                <span className="font-mono text-sm font-black text-[#FFD700]">{amount}</span>
            </span>
        </button>
    );
};

/**
 * CreditsChip — gold credit display with a subtle pulse animation
 * triggered whenever `credits` changes (e.g. after the AuthContext 20s poll
 * picks up a server-side change like a Betnex slot win, admin deposit, or bet
 * placed in another tab). Direction-aware: green tint on increase, red on decrease.
 */
// Free Play share of the balance, shown separately so it never looks like it disappeared
const FreePlayLine = ({ amount, testId }) => (
    <span className="flex flex-col leading-none border-l border-white/10 pl-2" title="Free Play is part of your balance. It is used first and must be played through once before winnings can be cashed out." data-testid={testId}>
        <span className="text-[8px] font-bold uppercase tracking-wider text-[#39FF14]/80 whitespace-nowrap">incl. Free Play</span>
        <span className="font-mono font-bold text-[#39FF14] text-sm">{amount}</span>
    </span>
);

const CreditsChip = ({ credits, bonusCredits = 0, variant = 'desktop' }) => {
    const navigate = useNavigate();
    const goBuy = () => navigate('/buy-credits');
    const low = (Number(credits) || 0) + (Number(bonusCredits) || 0) < 5;
    const prev = useRef(credits);
    const [flash, setFlash] = useState(null); // 'up' | 'down' | null
    const [jackpot, setJackpot] = useState(null); // { amount, key } | null

    useEffect(() => {
        if (typeof credits !== 'number' || typeof prev.current !== 'number') {
            prev.current = credits;
            return;
        }
        const delta = credits - prev.current;
        if (Math.abs(delta) < 0.005) return;
        const dir = delta > 0 ? 'up' : 'down';
        prev.current = credits;
        setFlash(dir);
        const t = setTimeout(() => setFlash(null), 900);
        return () => clearTimeout(t);
    }, [credits]);

    // Special "ka-ching" flash dispatched by Receipt approvals or daily bonuses
    // — provides a bigger gold glow + rising "+$X.XX" overlay than the routine
    // green up-pulse above. Listen to the global custom event so any feature
    // can opt in without prop drilling.
    useEffect(() => {
        const onKaching = (e) => {
            const amt = Number(e?.detail?.amount || 0);
            if (!(amt > 0)) return;
            setJackpot({ amount: amt, key: Date.now() });
            const t = setTimeout(() => setJackpot(null), 1800);
            return () => clearTimeout(t);
        };
        window.addEventListener('betohalics:credits-jackpot', onKaching);
        return () => window.removeEventListener('betohalics:credits-jackpot', onKaching);
    }, []);

    const totalCredits = (Number(credits) || 0) + (Number(bonusCredits) || 0);
    const value = totalCredits.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00';
    const bonus = Number(bonusCredits) || 0;
    const bonusStr = bonus.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    if (variant === 'mobile') {
        return (
            <div
                id="header-credits-chip-mobile"
                onClick={goBuy}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') goBuy(); }}
                title="Tap to add credits"
                className={`credits-chip credits-chip--mobile relative flex-1 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#121212] border border-white/10 cursor-pointer transition-colors hover:border-[#FFD700]/50 active:scale-[0.99] ${low ? 'credits-chip--low' : ''} ${flash ? `is-flashing flash-${flash}` : ''} ${jackpot ? 'credits-jackpot-flash' : ''}`}
                data-testid="credits-display"
                data-flash={flash || ''}
            >
                <Coins className="w-5 h-5 shrink-0 text-[#FFD700]" />
                <div className="flex flex-1 items-center justify-between gap-2 leading-tight">
                    <span className="flex flex-col">
                        <span className="text-[9px] font-bold uppercase tracking-wider text-white/50">Balance</span>
                        <span className="font-mono font-bold text-[#FFD700] credits-chip__value">{value}</span>
                    </span>
                    {bonus > 0 && <FreePlayLine amount={bonusStr} testId="credits-free-play-mobile" />}
                </div>
                {jackpot && (
                    <span key={jackpot.key} className="credits-jackpot-rise" aria-hidden="true">
                        +${jackpot.amount.toFixed(2)}
                    </span>
                )}
            </div>
        );
    }
    return (
        <div className="flex items-center gap-2">
        <div
            id="header-credits-chip-desktop"
            onClick={goBuy}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') goBuy(); }}
            className={`credits-chip relative flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-[#121212] border border-white/10 cursor-pointer transition-colors hover:border-[#FFD700]/50 active:scale-[0.99] ${low ? 'credits-chip--low' : ''} ${flash ? `is-flashing flash-${flash}` : ''} ${jackpot ? 'credits-jackpot-flash' : ''}`}
            data-testid="credits-display"
            data-flash={flash || ''}
            title={`$${value} balance — tap to add credits`}
        >
            <span className="flex items-center gap-1.5">
                <Coins className="w-4 h-4 text-[#FFD700]" />
                <span className="flex flex-col leading-none">
                    <span className="text-[8px] font-bold uppercase tracking-wider text-white/45">Balance</span>
                    <span className="font-mono font-bold text-[#FFD700] credits-chip__value">{value}</span>
                </span>
            </span>
            {bonus > 0 && <FreePlayLine amount={bonusStr} testId="credits-free-play-desktop" />}
            {jackpot && (
                <span key={jackpot.key} className="credits-jackpot-rise" aria-hidden="true">
                    +${jackpot.amount.toFixed(2)}
                </span>
            )}
        </div>
        {low && (
            <button onClick={goBuy} data-testid="low-balance-cta"
                className="load-money-pulse hidden md:inline-flex items-center gap-1 rounded-full bg-[#FFD700] px-3 py-1.5 text-xs font-black text-black">
                Top up to keep playing
            </button>
        )}
        </div>
    );
};

/**
 * PotContributionChip — tiny green chip that pops in for ~5s every time the
 * player's wager funds the community pots. Listens to the
 * `betohalics:pot-contribution` window event dispatched from wager call sites.
 *
 * Reinforces "every bet earns me a share of the next big payout".
 */
const PotContributionChip = () => {
    const [event, setEvent] = useState(null); // { total_add, grand_add, super_active, ... }
    const timerRef = useRef(null);

    useEffect(() => {
        return onPotContribution((detail) => {
            if (!detail || !(detail.total_add > 0)) return;
            setEvent({ ...detail, _id: Date.now() }); // _id forces remount on rapid-fire wagers
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => setEvent(null), 5000);
        });
    }, []);

    return (
        <AnimatePresence>
            {event && (
                <motion.div
                    key={event._id}
                    initial={{ opacity: 0, x: -12, scale: 0.85 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: 12, scale: 0.85 }}
                    transition={{ duration: 0.32, ease: 'easeOut' }}
                    className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#39FF14]/15 border border-[#39FF14]/50 shadow-[0_0_18px_rgba(57,255,20,0.35)]"
                    data-testid="pot-contribution-chip"
                    title={`${event.grand_add ? `+$${event.grand_add.toFixed(2)} Grand` : ''}${event.super_active ? ` · +$${(event.super_add || 0).toFixed(2)} Super` : ''}${event.blake_add ? ` · +$${event.blake_add.toFixed(2)} BETo` : ''}`}
                >
                    <Flame className="w-3.5 h-3.5 text-[#39FF14] flame-flicker shrink-0" />
                    <span className="font-mono font-black text-[#39FF14] text-xs tabular-nums">
                        +${event.total_add.toFixed(2)}
                    </span>
                    <span className="hidden lg:inline text-[10px] uppercase tracking-wider font-bold text-[#39FF14]/80">
                        you're in the pot
                    </span>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
